import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { LIMITS } from "@checklist/shared";
import { fieldCls } from "../AuthShell.js";
import { Button } from "../ui/Button.js";
import { Modal } from "../ui/Modal.js";
import { SearchInput } from "../ui/SearchInput.js";
import type { ApiChecklistSection } from "../../lib/api.js";
import { listDestinations, type DestinationRow, type MoveDestination } from "../../lib/checklistSelection.js";

const rowCls =
  "flex min-h-11 w-full items-center justify-between gap-3 rounded-lg border border-line bg-white px-3 py-2 text-left transition hover:border-brand-200 hover:bg-brand-50/40 disabled:cursor-not-allowed disabled:opacity-50";

export function MoveItemsModal({
  open,
  onClose,
  sections,
  selectedIds,
  busy,
  onMove,
}: {
  open: boolean;
  onClose: () => void;
  sections: ApiChecklistSection[];
  selectedIds: ReadonlySet<string>;
  busy: boolean;
  onMove: (destination: MoveDestination) => void | Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const [newSectionOpen, setNewSectionOpen] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const [newSubgroupFor, setNewSubgroupFor] = useState<number | null>(null);
  const [newSubgroupName, setNewSubgroupName] = useState("");

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setNewSectionOpen(false);
    setNewSectionName("");
    setNewSubgroupFor(null);
    setNewSubgroupName("");
  }, [open]);

  const rows = useMemo(() => listDestinations(sections, selectedIds), [sections, selectedIds]);
  const rowFor = (sectionIdx: number, subgroupId: string | null): DestinationRow | undefined =>
    rows.find((r) => r.sectionIdx === sectionIdx && r.subgroupId === subgroupId);

  const count = selectedIds.size;
  const queryLower = query.trim().toLowerCase();
  const atSectionLimit = sections.length >= LIMITS.templateSectionsMax;

  function commitMove(destination: MoveDestination) {
    void onMove(destination);
  }

  function handleCreateSectionKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      const name = newSectionName.trim();
      if (name) commitMove({ kind: "newSection", name });
    }
  }

  function handleCreateSubgroupKeyDown(e: KeyboardEvent<HTMLInputElement>, sectionIdx: number) {
    if (e.key === "Enter") {
      e.preventDefault();
      const name = newSubgroupName.trim();
      if (name) commitMove({ kind: "newSubgroup", sectionIdx, name });
    }
  }

  return (
    <Modal open={open} onClose={onClose} labelledBy="moveItemsTitle">
      <h2 id="moveItemsTitle" className="text-lg font-semibold text-ink">
        Move {count} item{count === 1 ? "" : "s"}
      </h2>

      <SearchInput
        className="mt-4 max-w-none"
        value={query}
        onChange={setQuery}
        placeholder="Search destinations…"
        aria-label="Search destinations"
      />

      <div className="mt-4 space-y-4">
        {newSectionOpen ? (
          <div className="flex items-center gap-2 rounded-lg border border-dashed border-line px-3 py-2">
            <input
              autoFocus
              className={`${fieldCls} !mt-0`}
              placeholder="New section name"
              value={newSectionName}
              maxLength={120}
              disabled={busy}
              onChange={(e) => setNewSectionName(e.target.value)}
              onKeyDown={handleCreateSectionKeyDown}
            />
            <Button
              type="button"
              size="sm"
              disabled={busy || !newSectionName.trim()}
              onClick={() => {
                const name = newSectionName.trim();
                if (name) commitMove({ kind: "newSection", name });
              }}
            >
              Create
            </Button>
          </div>
        ) : (
          <button
            type="button"
            className={rowCls}
            disabled={busy || atSectionLimit}
            title={atSectionLimit ? `Checklists can have at most ${LIMITS.templateSectionsMax} sections.` : undefined}
            onClick={() => setNewSectionOpen(true)}
          >
            <span className="text-sm font-medium text-brand-700">+ New section…</span>
          </button>
        )}

        {sections.map((section, sectionIdx) => {
          const subgroups = section.subgroups ?? [];
          const sectionNameMatches = !queryLower || section.name.toLowerCase().includes(queryLower);
          const matchingSubgroups = queryLower
            ? subgroups.filter((sg) => sg.name.toLowerCase().includes(queryLower))
            : subgroups;
          const visibleSubgroups = sectionNameMatches ? subgroups : matchingSubgroups;
          const sectionVisible = sectionNameMatches || matchingSubgroups.length > 0;
          if (!sectionVisible) return null;

          const sectionRow = rowFor(sectionIdx, null);
          const atSubgroupLimit = subgroups.length >= LIMITS.subgroupsMax;

          return (
            <div key={sectionIdx} className="space-y-1.5">
              <button
                type="button"
                className={rowCls}
                disabled={busy}
                onClick={() => commitMove({ kind: "existing", sectionIdx, subgroupId: null })}
              >
                <span className="text-sm font-semibold text-ink">{section.name}</span>
                <span className="flex shrink-0 items-center gap-2 text-xs text-muted-sm">
                  {sectionRow?.isCurrentHome ? (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-muted">Here</span>
                  ) : null}
                  {sectionRow?.itemCount ?? 0} item{(sectionRow?.itemCount ?? 0) === 1 ? "" : "s"}
                </span>
              </button>

              {visibleSubgroups.map((sg) => {
                const sgRow = rowFor(sectionIdx, sg.id);
                return (
                  <button
                    key={sg.id}
                    type="button"
                    className={`${rowCls} ml-4`}
                    disabled={busy}
                    onClick={() => commitMove({ kind: "existing", sectionIdx, subgroupId: sg.id })}
                  >
                    <span className="text-sm text-ink">{sg.name}</span>
                    <span className="flex shrink-0 items-center gap-2 text-xs text-muted-sm">
                      {sgRow?.isCurrentHome ? (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-muted">Here</span>
                      ) : null}
                      {sgRow?.itemCount ?? 0} item{(sgRow?.itemCount ?? 0) === 1 ? "" : "s"}
                    </span>
                  </button>
                );
              })}

              {newSubgroupFor === sectionIdx ? (
                <div className="ml-4 flex items-center gap-2 rounded-lg border border-dashed border-line px-3 py-2">
                  <input
                    autoFocus
                    className={`${fieldCls} !mt-0`}
                    placeholder="New subgroup name"
                    aria-label="New subgroup name"
                    value={newSubgroupName}
                    maxLength={LIMITS.subgroupNameMax}
                    disabled={busy}
                    onChange={(e) => setNewSubgroupName(e.target.value)}
                    onKeyDown={(e) => handleCreateSubgroupKeyDown(e, sectionIdx)}
                  />
                  <Button
                    type="button"
                    size="sm"
                    disabled={busy || !newSubgroupName.trim()}
                    onClick={() => {
                      const name = newSubgroupName.trim();
                      if (name) commitMove({ kind: "newSubgroup", sectionIdx, name });
                    }}
                  >
                    Create
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  className={`${rowCls} ml-4`}
                  disabled={busy || atSubgroupLimit}
                  title={atSubgroupLimit ? `Sections can have at most ${LIMITS.subgroupsMax} subgroups.` : undefined}
                  onClick={() => {
                    setNewSubgroupFor(sectionIdx);
                    setNewSubgroupName("");
                  }}
                >
                  <span className="text-sm font-medium text-brand-700">
                    + New subgroup in "{section.name}"…
                  </span>
                </button>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
